import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, EyeOffIcon } from 'lucide-react';
import { Density, Field } from '../types';
import { InlineField } from './InlineField';
import { resolveIcon } from '../utils/icons';
interface FormSectionProps {
  title: string;
  icon: string;
  fields: Field[];
  columns: 1 | 2;
  density: Density;
  defaultCollapsed: boolean;
  /** Count of fields currently hidden by admin config or rules */
  hiddenCount: number;
  values: Record<string, string | boolean>;
  dirtyIds: Set<string>;
  onChange: (id: string, value: string | boolean) => void;
}
export function FormSection({
  title,
  icon,
  fields,
  columns,
  density,
  defaultCollapsed,
  hiddenCount,
  values,
  dirtyIds,
  onChange
}: FormSectionProps) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const Icon = resolveIcon(icon);
  const compact = density === 'compact';
  useEffect(() => {
    setOpen(!defaultCollapsed);
  }, [defaultCollapsed]);
  const editedCount = fields.filter(f => dirtyIds.has(f.id)).length;
  return <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-id="element-108" data-mp="2byo62151rjv8">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className={`flex w-full items-center gap-3 text-left transition-colors hover:bg-slate-50 ${compact ? 'px-4 py-3' : 'px-5 py-4'}`} data-id="element-109" data-mp="271nzzq876h44">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600" data-id="element-110" data-mp="2uqgpp4y87sqa">
          <Icon className="h-[18px] w-[18px]" data-id="element-111" data-mp="2qnu4hmw29622" />
        </span>
        <div className="min-w-0 flex-1" data-id="element-112" data-mp="2qqgcfnmv5nkt">
          <h2 className="text-[15px] font-semibold text-slate-900" data-id="element-113" data-mp="2lqbv5ve4sotj">{title}</h2>
          <p className="flex items-center gap-1.5 text-xs text-slate-400" data-id="element-114" data-mp="1f96p2q0m6be8">
            {fields.length} fields
            {hiddenCount > 0 && <span className="inline-flex items-center gap-1 text-slate-400" data-id="element-115" data-mp="1a8skjhy81jky">
                <EyeOffIcon className="h-3 w-3" data-id="element-116" data-mp="1bskg3m2uh5xz" />
                {hiddenCount} hidden
              </span>}
          </p>
        </div>
        {editedCount > 0 && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700" data-id="element-117" data-mp="12rdvp1x2razg">
            {editedCount} edited
          </span>}
        <ChevronDownIcon className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-118" data-mp="16uai73pqjr8o" />
      </button>

      <AnimatePresence initial={false} data-id="element-119" data-mp="xtuj06g0z69m">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-120" data-mp="j3wrbgivzyfo">
            <div className={`border-t border-slate-100 ${compact ? 'p-1.5' : 'p-2 sm:p-3'}`} data-id="element-121" data-mp="f1jiukcr9mf7">
              {fields.length === 0 ? <p className="px-3 py-8 text-center text-sm text-slate-400" data-id="element-122" data-mp="3rl4ep40x8afo">
                  No visible fields in this section.
                </p> : <div className={`grid grid-cols-1 gap-0.5 ${columns === 2 ? 'md:grid-cols-2' : ''}`} data-id="element-123" data-mp="4h7q9a8zld76">
                  {fields.map(field => <InlineField key={field.id} field={field} value={values[field.id]} dirty={dirtyIds.has(field.id)} compact={compact} onChange={onChange} data-id="element-124" data-mp="ecrlrvjn29ve" />)}
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}